import { atom, useRecoilState } from "recoil";

interface UploadFileState {
    fileName: string;
    uploadedChunks: number;
    totalChunks: number;
    status: "pending" | "uploading" | "completed" | "failed";
    errorMessage?: string;
}

interface UploadState {
    showBanner: boolean;
    files: UploadFileState[];
    controller?: AbortController;
}

export const DEFAULT_UPLOAD_STATE: UploadState = {
    showBanner: false,
    files: [],
    controller: undefined,
};

const uploadStateAtom = atom<UploadState>({
    key: "uploadState",
    default: DEFAULT_UPLOAD_STATE,
});

const useUploadState = () => {
    const [state, setState] = useRecoilState(uploadStateAtom);

    const resetStateToDefault = () => {
        setState((prev) => ({
            ...DEFAULT_UPLOAD_STATE,
            controller: prev.controller,
        }));
    };

    const addFile = (fileName: string, totalChunks: number) => {
        setState((prev) => ({
            ...prev,
            showBanner: true,
            files: [
                ...prev.files,
                {
                    fileName,
                    uploadedChunks: 0,
                    totalChunks,
                    status: "pending",
                },
            ],
        }));
    };

    const updateFileProgress = (fileName: string, uploadedChunks: number) => {
        setState((prev) => ({
            ...prev,
            files: prev.files.map((file) =>
                file.fileName === fileName ? { ...file, uploadedChunks, status: "uploading" as const } : file
            ),
        }));
    };

    const setFileCompleted = (fileName: string) => {
        setState((prev) => ({
            ...prev,
            files: prev.files.map((file) =>
                file.fileName === fileName
                    ? {
                          ...file,
                          status: "completed" as const,
                          uploadedChunks: file.totalChunks,
                      }
                    : file
            ),
        }));
    };

    const setFileFailed = (fileName: string, errorMessage: string) => {
        setState((prev) => ({
            ...prev,
            files: prev.files.map((file) =>
                file.fileName === fileName ? { ...file, status: "failed" as const, errorMessage } : file
            ),
        }));
    };

    const removeFile = (fileName: string) => {
        setState((prev) => {
            const newFiles = prev.files.filter((f) => f.fileName !== fileName);
            return {
                ...prev,
                files: newFiles,
                showBanner: newFiles.length > 0,
            };
        });
    };

    const getOverallProgress = () => {
        if (state.files.length === 0) return 0;
        const totalChunks = state.files.reduce((acc, file) => acc + file.totalChunks, 0);
        const uploadedChunks = state.files.reduce((acc, file) => acc + file.uploadedChunks, 0);
        return totalChunks > 0 ? Math.round((uploadedChunks / totalChunks) * 100) : 0;
    };

    const isAllCompleted = () => state.files.length > 0 && state.files.every((file) => file.status === "completed");

    const hasAnyFailed = () => state.files.some((file) => file.status === "failed");

    return {
        state,
        setState,
        resetStateToDefault,
        addFile,
        updateFileProgress,
        setFileCompleted,
        setFileFailed,
        removeFile,
        getOverallProgress,
        isAllCompleted,
        hasAnyFailed,
    };
};

export default useUploadState;
